// /admin/account-managers — operator console: the INHERTA team and their client
// portfolios. A team KPI strip + a card per manager with load,
// availability and today's attention. Search + availability/level filters.
// Data via the getAccountManagers() seam, wired to GET /admin/account-managers
// (portfolio + load are real; some card fields default until a source exists).
// The row actions (portfolio, reassign, level) have no backend yet and show
// coming-soon toasts. Team invites live at /admin/usuarios now — this page's
// header action just navigates there.

import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { type AccountManager, type AmKpi, getAccountManagers } from './account-managers-data.js'
import {
  Button,
  Chip,
  DropdownMenu,
  FilterBar,
  PageHeader,
  ProgressBar,
  SearchInput,
  Select,
  Spinner,
} from '../../design-system/index.js'
import { matchesSearch } from '../../lib/search.js'

const BANNER_STYLE = {
  warn: 'bg-warn-bg text-warn',
  ok: 'bg-ok-bg text-ok',
  away: 'bg-field text-text-2',
} as const

const LOAD_TONE = { warn: 'warn', ok: 'ok', mut: 'brand' } as const

function KpiStat({ kpi }: { kpi: AmKpi }): JSX.Element {
  return (
    <div className="rounded-lg border border-line bg-surface px-[18px] py-4">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.05em] text-text-3">
        {kpi.label}
      </div>
      <div className="mt-1.5 text-[24px] font-extrabold leading-none tabular-nums text-text">
        {kpi.value}
        {kpi.unit && <span className="text-[15px] font-semibold text-text-2">{kpi.unit}</span>}
      </div>
      <div
        className={`mt-1.5 text-[11px] ${kpi.subWarn ? 'font-semibold text-warn' : 'text-text-2'}`}
      >
        {kpi.sub}
      </div>
    </div>
  )
}

function ManagerCard({ am }: { am: AccountManager }): JSX.Element {
  const navigate = useNavigate()
  return (
    <article className="flex flex-col rounded-xl border border-line bg-surface p-4">
      <div className="flex items-start gap-3">
        <span className="relative flex-none">
          <span
            aria-hidden
            className={`grid size-10 place-items-center rounded-full text-[12px] font-bold text-white ${
              am.availability === 'away' ? 'opacity-60' : ''
            } bg-[image:var(--brand-gradient)]`}
          >
            {am.initials}
          </span>
          <span
            aria-hidden
            className={`absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-surface ${
              am.availability === 'active' ? 'bg-ok' : 'bg-text-3'
            }`}
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13.5px] font-bold text-text">{am.name}</span>
            <span
              className={`rounded-pill px-1.5 py-px text-[9.5px] font-bold ${
                am.level === 'Senior'
                  ? 'bg-[color-mix(in_srgb,var(--brand-blue)_13%,transparent)] text-link'
                  : 'bg-field text-text-2'
              }`}
            >
              {am.level}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-text-3">{am.role}</p>
        </div>
        <Chip tone={am.availability === 'active' ? 'ok' : 'draft'} dot>
          {am.availabilityLabel}
        </Chip>
      </div>

      <a
        href={`mailto:${am.email}`}
        className="mt-2.5 flex items-center gap-1.5 text-[11px] text-text-2 hover:text-link [&_svg]:size-[12px]"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <rect
            x="2"
            y="4.5"
            width="20"
            height="15"
            rx="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="m2.7 6.5 9.3 6 9.3-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {am.email}
      </a>

      {am.banner && (
        <div
          className={`mt-2.5 flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] leading-snug ${BANNER_STYLE[am.banner.tone]}`}
        >
          {am.banner.text}
        </div>
      )}

      <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-surface-2 px-3 py-2.5 text-center">
        {[
          { l: 'Clientes', v: String(am.clients), attn: false },
          { l: 'Hoy', v: am.today === null ? '—' : String(am.today), attn: (am.today ?? 0) > 0 },
          { l: 'Resp. promedio', v: am.avgResponse, attn: false },
        ].map((s) => (
          <div key={s.l}>
            <div
              className={`text-[14px] font-extrabold tabular-nums ${s.attn ? 'text-warn' : 'text-text'}`}
            >
              {s.v}
            </div>
            <div className="mt-0.5 text-[9.5px] text-text-3">{s.l}</div>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[11px]">
          <span className="text-text-2">Carga</span>
          <span
            className={am.loadTone === 'warn' ? 'font-bold text-warn' : 'font-semibold text-text-2'}
          >
            {am.loadPct}%
          </span>
        </div>
        <ProgressBar value={am.loadPct} tone={LOAD_TONE[am.loadTone]} />
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-line pt-3">
        <span className="flex flex-1 items-center gap-1 text-[10.5px] text-text-3 [&_svg]:size-[11px]">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
            <circle cx="12" cy="12" r="9" strokeLinecap="round" />
            <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {am.lastActive}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void navigate(`/admin/tenants?q=${encodeURIComponent(am.name)}`)}
        >
          Ver portafolio
        </Button>
        {/* "Reasignar clientes" and "Cambiar nivel" used to live here as
            coming-soon toasts. Reassigning is done PER CLIENT (Clientes → el
            cliente → Editar → Gerente de cuenta) and already works, so a dead
            menu item here told the operator the opposite of the truth. Changing
            a manager's level has no backend at all. Both removed rather than
            left as doors that do not open. */}
        <DropdownMenu
          trigger={
            <button
              type="button"
              aria-label={`Más acciones de ${am.name}`}
              className="flex size-8 flex-none items-center justify-center rounded-md border border-line text-text-3 transition-colors hover:bg-hover hover:text-text [&_svg]:size-[15px]"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </button>
          }
          items={[
            {
              label: 'Ver sus clientes',
              onSelect: () => void navigate(`/admin/tenants?q=${encodeURIComponent(am.name)}`),
            },
          ]}
        />
      </div>
    </article>
  )
}

export function AccountManagersPage(): JSX.Element {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'account-managers'],
    queryFn: getAccountManagers,
  })
  const [query, setQuery] = useState('')
  const [availability, setAvailability] = useState('')
  const [level, setLevel] = useState('')

  const managers = data?.managers ?? []
  const visible = useMemo(
    () =>
      managers.filter(
        (m) =>
          matchesSearch(m.name, query) &&
          (availability === '' || m.availability === availability) &&
          (level === '' || m.level === level),
      ),
    [managers, query, availability, level],
  )

  if (isLoading || !data) {
    return (
      <div data-testid="admin-account-managers" className="mx-auto w-full max-w-6xl py-10">
        <Spinner label="Cargando gerentes…" />
      </div>
    )
  }

  return (
    <div data-testid="admin-account-managers" className="mx-auto w-full max-w-6xl">
      <PageHeader
        title="Gerentes de cuenta"
        subtitle="El equipo INHERTA y sus carteras."
        actions={
          <Button onClick={() => void navigate('/admin/usuarios')}>Invitar al equipo</Button>
        }
      />

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {data.kpis.map((k) => (
          <KpiStat key={k.label} kpi={k} />
        ))}
      </div>

      <div className="mt-4">
        <FilterBar>
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar gerente"
            aria-label="Buscar gerente"
          />
          <div className="w-full sm:w-44">
            <Select
              aria-label="Filtrar por disponibilidad"
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              options={[
                { value: '', label: 'Toda disponibilidad' },
                { value: 'active', label: 'Activos' },
                { value: 'away', label: 'Ausentes' },
              ]}
            />
          </div>
          <div className="w-full sm:w-40">
            <Select
              aria-label="Filtrar por nivel"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              options={[
                { value: '', label: 'Todos los niveles' },
                { value: 'Senior', label: 'Senior' },
                { value: 'Gerente', label: 'Gerente' },
                { value: 'Junior', label: 'Junior' },
              ]}
            />
          </div>
        </FilterBar>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((am) => (
          <ManagerCard key={am.id} am={am} />
        ))}
        {visible.length === 0 && (
          <p className="col-span-full py-10 text-center text-[12.5px] text-text-3">
            Ningún gerente coincide.
          </p>
        )}
      </div>
    </div>
  )
}
